<script setup lang="ts">
import type HtmlTag from '~/types/htmlTag'

type LabelHtmlTag = Extract<HtmlTag, 'div' | 'label'>

/**
 * Props for VLabel component.
 * @property htmlTag - The html tag to wrap the underlying content with. Defaults to "div".
 */
type Props = {
  htmlTag?: LabelHtmlTag
}

withDefaults(defineProps<Props>(), {
  htmlTag: 'div' as LabelHtmlTag,
})
</script>

<script lang="ts">
/**
 * Displays a label/title with the same styles as an input label but not the same markup.
 * If you're making a label for an input, use the "LabelInput" component instead.
 *
 * @example
 * <VLabel>
 *   Music Genres
 *   <template #subLabel>(optional)</template>
 * </VLabel>
 */
export default {}
</script>

<template>
  <component
    :is="htmlTag"
    class="tw-relative tw-flex tw-w-full tw-flex-wrap tw-items-center tw-justify-start tw-leading-5"
  >
    <span
      class="tw-text-body tw-mr-2 tw-min-h-xl tw-font-medium"
      data-test-id="label"
    >
      <slot />
    </span>
    <span
      v-if="$slots.subLabel"
      class="tw-text-body-sm tw-font-medium"
      data-test-id="labelAnnotation"
    >
      <slot name="subLabel" />
    </span>
  </component>
</template>
